import React, { useEffect, useMemo, useState } from 'react';
import { Link } from "react-router-dom";

import BasicCRUD from './../core/basic-crud';
import { socketUseEffect } from "../../socket";
import BadgeStatusView from "./sub-pages/elements/BadgeStatusView";
import { useAuth } from "../../AuthContext";
import ProgressBar from "../page-elements/progress-bar";
import FadeAlert from "../core/fade-alert";

// ---------- i18n ----------
const dict = {
    en: {
        // page
        title: "WordPress",
        subTitle: "add / edit WordPress sites",
        homeURL: "/dashboard",
        addTitle: "Add WordPress Site",
        editTitle: "Edit WordPress Site",

        // columns
        colStatus: "Status",
        colName: "Name",
        colCurrentVersion: "Current Version",
        colPlugins: "Plugins",
        colTheme: "Theme",
        colUpdates: "Updates",
        colContactGroup: "Contact Group",

        // cells
        wpVersionPrefix: "WordPress",
        details: "WordPress Details",
        lastUpdate: "Last Update:",
        nextUpdate: "Next Update:",
        invalidDate: "Invalid Date",
        na: "N/A",

        // form
        tabMain: "Main",
        fieldName: "Name",
        fieldContactGroup: "Contact Group",
        fieldURL: "URL",
        fieldToken: "Token",
        basicAuth: "Basic Authorization",
        login: "Login",
        password: "Password",
        validatedAt: "Validated At",
        dayOfWeek: "Day of Week",
        dayOfMonth: "Day of Month",
        time: "Time",

        // selects
        choose: " -- choose -- ",
        yes: "Yes",
        no: "No",
        daily: "Daily",
        weekly: "Weekly",
        monthly: "Monthly",
        dow: {
            monday: "Monday",
            tuesday: "Tuesday",
            wednesday: "Wednesday",
            thursday: "Thursday",
            friday: "Friday",
            saturday: "Saturday",
            sunday: "Sunday",
        },

        // actions
        edit: "Edit",
        delete: "Delete",

        // quota/plan
        noResources: "You don't have any available resources. Please change your pricing plan to get more items.",
        planExpired: "Your pricing plan has expired. Please renew or upgrade your plan to continue accessing this feature.",

        // delete dialog
        delAlert: "Deleting a WordPress site cannot be undone.",
        delText: "To confirm deletion, enter the name of the WordPress site in the next input field",
        delTitle: 'Delete WordPress site: "{name}"',

        // toasts
        toastEntity: "WordPress Site",
        createdOk: "created successfully",
        updatedOk: "updated successfully",
        deletedOk: "deleted successfully",
    },

    ru: {
        title: "WordPress",
        subTitle: "добавление / редактирование сайтов WordPress",
        homeURL: "/dashboard",
        addTitle: "Добавить сайт WordPress",
        editTitle: "Редактировать сайт WordPress",

        colStatus: "Статус",
        colName: "Название",
        colCurrentVersion: "Текущая версия",
        colPlugins: "Плагины",
        colTheme: "Тема",
        colUpdates: "Обновления",
        colContactGroup: "Группа контактов",

        wpVersionPrefix: "WordPress",
        details: "Детали WordPress",
        lastUpdate: "Последнее обновление:",
        nextUpdate: "Следующее обновление:",
        invalidDate: "Неверная дата",
        na: "Н/Д",

        tabMain: "Основное",
        fieldName: "Название",
        fieldContactGroup: "Группа контактов",
        fieldURL: "URL",
        fieldToken: "Токен",
        basicAuth: "Базовая авторизация",
        login: "Логин",
        password: "Пароль",
        validatedAt: "Проверять",
        dayOfWeek: "День недели",
        dayOfMonth: "День месяца",
        time: "Время",

        choose: " -- выберите -- ",
        yes: "Да",
        no: "Нет",
        daily: "Ежедневно",
        weekly: "Еженедельно",
        monthly: "Ежемесячно",
        dow: {
            monday: "Понедельник",
            tuesday: "Вторник",
            wednesday: "Среда",
            thursday: "Четверг",
            friday: "Пятница",
            saturday: "Суббота",
            sunday: "Воскресенье",
        },

        edit: "Редактировать",
        delete: "Удалить",

        noResources: "У вас нет доступных ресурсов. Пожалуйста, измените тарифный план, чтобы получить больше элементов.",
        planExpired: "Срок действия вашего тарифного плана истёк. Обновите или продлите план, чтобы продолжить пользоваться функцией.",

        delAlert: "Удаление сайта WordPress необратимо.",
        delText: "Чтобы подтвердить удаление, введите название сайта WordPress в следующее поле ввода",
        delTitle: 'Удалить сайт WordPress: «{name}»',

        toastEntity: "Сайт WordPress",
        createdOk: "успешно создан",
        updatedOk: "успешно обновлён",
        deletedOk: "успешно удалён",
    },

    ua: {
        title: "WordPress",
        subTitle: "додавання / редагування сайтів WordPress",
        homeURL: "/dashboard",
        addTitle: "Додати сайт WordPress",
        editTitle: "Редагувати сайт WordPress",

        colStatus: "Статус",
        colName: "Назва",
        colCurrentVersion: "Поточна версія",
        colPlugins: "Плагіни",
        colTheme: "Тема",
        colUpdates: "Оновлення",
        colContactGroup: "Група контактів",

        wpVersionPrefix: "WordPress",
        details: "Деталі WordPress",
        lastUpdate: "Останнє оновлення:",
        nextUpdate: "Наступне оновлення:",
        invalidDate: "Невірна дата",
        na: "Н/Д",

        tabMain: "Основне",
        fieldName: "Назва",
        fieldContactGroup: "Група контактів",
        fieldURL: "URL",
        fieldToken: "Токен",
        basicAuth: "Базова авторизація",
        login: "Логін",
        password: "Пароль",
        validatedAt: "Перевіряти",
        dayOfWeek: "День тижня",
        dayOfMonth: "День місяця",
        time: "Час",

        choose: " -- оберіть -- ",
        yes: "Так",
        no: "Ні",
        daily: "Щоденно",
        weekly: "Щотижнево",
        monthly: "Щомісяця",
        dow: {
            monday: "Понеділок",
            tuesday: "Вівторок",
            wednesday: "Середа",
            thursday: "Четвер",
            friday: "П’ятниця",
            saturday: "Субота",
            sunday: "Неділя",
        },

        edit: "Редагувати",
        delete: "Видалити",

        noResources: "У вас немає доступних ресурсів. Будь ласка, змініть тарифний план, щоб отримати більше елементів.",
        planExpired: "Строк дії вашого тарифного плану минув. Поновіть або оновіть план, щоб продовжити користування функцією.",

        delAlert: "Видалення сайту WordPress неможливо скасувати.",
        delText: "Щоб підтвердити видалення, введіть назву сайту WordPress у наступне поле вводу",
        delTitle: 'Видалити сайт WordPress: «{name}»',

        toastEntity: "Сайт WordPress",
        createdOk: "успішно створено",
        updatedOk: "успішно оновлено",
        deletedOk: "успішно видалено",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ---------- component ----------
const Wordpress = ({ lang: langProp, ...props }) => {
    const { user } = useAuth();
    const lang = (langProp || getInitialLang());
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const object = "wordpress";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});

    const [totalData, setTotalData] = useState([]);
    const [contacts, setContacts] = useState([{ id: 0, name: t.choose }]);

    const listeners = [
        { name: "contacts", fn: "items-list", args: { user_id: user.user_id, site_id: props.site_id }, saveDataFN: setContacts },
        { name: "dashboard", fn: "reports-total", args: {}, saveDataFN: setTotalData },
    ];
    useEffect(() => socketUseEffect(listeners, user), [response?.status, response?.created, response?.updated, response?.delete]); // eslint-disable-line

    const formatStatus = (data) => <BadgeStatusView status={data.status} lang={lang} />;

    const formatNameAndUrl = (data) => {
        const name = data.name || t.na;
        const url = data.url || t.na;

        const styles = {
            container: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
            name: { fontWeight: 'bold', fontSize: 16, marginBottom: 4 },
            url: { fontSize: 12, color: '#6c757d' },
        };

        return (
            <div style={styles.container}>
                <Link to={`/wordpress/${data.id}/dashboard`} className="sidebar-link" data-text={t.details}>
                    <span style={styles.name}>{name}</span>
                </Link>
                <span style={styles.url}>{url}</span>
            </div>
        );
    };

    const formatCurrentVersion = (data) => {
        const version = data.current_version || t.na;
        const box = {
            display: 'inline-block',
            padding: '5px 10px',
            borderRadius: 8,
            backgroundColor: '#e0f7fa',
            color: '#00796b',
            fontWeight: 'bold',
            fontSize: 14,
            border: '1px solid #b2ebf2',
        };
        return <span style={box}>{t.wpVersionPrefix} {version}</span>;
    };

    const formatUpdateCount = (data, key) => {
        const count = data[key] ?? 0;
        const box = {
            display: 'inline-block',
            padding: '5px 10px',
            borderRadius: 8,
            backgroundColor: '#f1f8e9',
            color: '#33691e',
            fontWeight: 'bold',
            fontSize: 14,
            textAlign: 'center',
        };
        return <span style={box}>{count}</span>;
    };

    const formatUpdateDates = (data) => {
        const toUS = (s) => {
            if (!s) return t.na;
            const d = new Date(s);
            return isNaN(d) ? t.invalidDate : d.toLocaleDateString('en-US');
        };

        const lastU = toUS(data.last_update);
        const nextU = toUS(data.next_update);

        const styles = {
            container: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' },
            label: { fontWeight: 'bold', fontSize: 14, color: '#333' },
            date: { fontSize: 12, color: '#6c757d' },
            row: { marginBottom: 4 },
        };

        return (
            <div style={styles.container}>
                <div style={styles.row}>
                    <span style={styles.label}>{t.lastUpdate}</span>
                    <span style={styles.date}>{lastU}</span>
                </div>
                <div style={styles.row}>
                    <span style={styles.label}>{t.nextUpdate}</span>
                    <span style={styles.date}>{nextU}</span>
                </div>
            </div>
        );
    };

    const columns = [
        { label: t.colStatus, key: "status", format: formatStatus },
        { label: t.colName, key: "name", format: formatNameAndUrl },
        { label: t.colCurrentVersion, key: "current_version", format: formatCurrentVersion },
        { label: t.colPlugins, key: "total_updated_plugins", format: formatUpdateCount },
        { label: t.colTheme, key: "total_updated_theme", format: formatUpdateCount },
        { label: t.colUpdates, key: "last_update", format: formatUpdateDates },
        { label: t.colContactGroup, key: "contacts_group_name" },
    ];

    const showField = (data, field) => {
        const key = field.key;
        const updated = { ...field };
        if ((key === "basic_auth_username" || key === "basic_auth_password") && String(data.basic_auth) === "0") {
            updated.readonly = true;
        }
        return updated;
    };

    const showRule = (data, key) => {
        if ((key === "validate_day_of_week" || key === "validate_day_of_month") && data.validate_at === "daily") return false;
        if (key === "validate_day_of_month" && data.validate_at === "weekly") return false;
        if (key === "validate_day_of_week" && data.validate_at === "monthly") return false;
        return true;
    };

    const daysOfMonth = Array.from({ length: 31 }, (_, i) => {
        const day = i + 1;
        return { id: day, name: String(day) };
    });

    const fields = [
        {
            type: "tab",
            name: t.tabMain,
            image: "",
            showField,
            showRule,
            fields: [
                [
                    { label: t.fieldName, key: "name", default_value: "", showLabel: "top" },
                    { label: t.fieldContactGroup, key: "contact_group_id", default_value: "0", type: "selectbox", data: contacts, showLabel: "top" },
                ],
                [],
                [
                    { label: t.fieldURL, key: "url", default_value: "", showLabel: "top" },
                    { label: t.fieldToken, key: "token", default_value: "", showLabel: "top" },
                ],
                [
                    { label: t.basicAuth, key: "basic_auth", type: "selectbox", data: [{ id: '1', name: t.yes }, { id: '0', name: t.no }], default_value: "0", showLabel: "top" },
                    { label: t.login, key: "basic_auth_username", default_value: "", showLabel: "top" },
                    { label: t.password, key: "basic_auth_password", default_value: "", showLabel: "top" },
                ],
                [
                    { label: t.validatedAt, key: "validate_at", default_value: "daily", type: "selectbox",
                        data: [{ id: "daily", name: t.daily }, { id: "weekly", name: t.weekly }, { id: "monthly", name: t.monthly }],
                        showLabel: "top" },
                    { label: t.dayOfWeek, key: "validate_day_of_week", default_value: "monday", type: "selectbox",
                        data: [
                            { id: "monday", name: t.dow.monday }, { id: "tuesday", name: t.dow.tuesday }, { id: "wednesday", name: t.dow.wednesday },
                            { id: "thursday", name: t.dow.thursday }, { id: "friday", name: t.dow.friday }, { id: "saturday", name: t.dow.saturday },
                            { id: "sunday", name: t.dow.sunday }
                        ],
                        showLabel: "top" },
                    { label: t.dayOfMonth, key: "validate_day_of_month", default_value: "1", type: "selectbox", data: daysOfMonth, showLabel: "top" },
                    { label: t.time, key: "validate_time", type: "time", default_value: "", showLabel: "top" },
                ],
            ],
        },
    ];

    const actions = [
        { name: "edit", label: t.edit, controller: "crud" },
        { name: "delete", label: t.delete, controller: "crud" },
    ];

    const expired = user?.expired ?? 1;
    const disabled = expired
        ? { add: { disable: true, title: t.planExpired } }
        : {
            add: {
                disable: (totalData.wordpress - totalData.used_wordpress) < 1,
                title: t.noResources,
            },
        };

    return (
        <>
            <BasicCRUD
                singleArgs={props?.singleArgs ?? false}
                title={t.title}
                subTitle={t.subTitle}
                homeURL={t.homeURL}
                addTitle={t.addTitle}
                disable={disabled}
                editTitle={t.editTitle}
                object={object}
                columns={columns}
                fields={fields}
                actions={actions}
                fieldsData={fieldsData}
                setFieldsData={setFieldsData}
                response={response}
                setResponse={setResponse}
                ownHandler={true}
                deleteConfirmationDialog={{
                    alert: t.delAlert,
                    text: t.delText,
                    title: t.delTitle,
                    column: 'name',
                }}
            >
                {!props.singleArgs && (
                    <ProgressBar lang={lang} used={totalData.used_wordpress} allowed={totalData.wordpress} />
                )}

                {response.status === 1 ? (
                    <FadeAlert response={response} resetResponse={() => setResponse([])}>
                        {response.created === 1 && (<><strong>{t.toastEntity}</strong> {t.createdOk}</>)}
                        {response.updated === 1 && (<><strong>{t.toastEntity}</strong> {t.updatedOk}</>)}
                        {response.delete === 1 && (<><strong>{t.toastEntity}</strong> {t.deletedOk}</>)}
                    </FadeAlert>
                ) : null}
            </BasicCRUD>
        </>
    );
};

export default Wordpress;
